import i18next from 'i18next'
import { Map } from 'immutable'
import * as React from 'react'
import { Updater, loadingAsyncState } from 'widgets-for-react'
import { IconButtonSocial } from '../../../utils/buttons'
import { ExternalLink } from '../../../utils/externalLink'
import { FormNotification, formSkeletonConfig } from '../../../utils/forms'
import { SVGIcon } from '../../../utils/svgicon'
import { slugify } from '../../../utils/utils'
import { LanguageSwitch } from '../header/headerLayout'
import { submitSubscription } from './footerApi'
import { FooterItem, FooterState } from './footerState'

export type FooterLayoutProps = FooterState & {
  setState: (_: Updater<FooterState>) => void
  isLoggedIn: boolean
  languageSwitchMode: 'closed' | 'open'
  setLanguageSwitchMode: (_: 'closed' | 'open') => void
  onLanguageChanged: () => void
}

function handleSubmit(
  e: React.FormEvent<HTMLFormElement>,
  setState: (_: Updater<FooterState>) => void
) {
  e.preventDefault()
  const target = e.target as typeof e.target & {
    email: { value: string }
  }
  const email = target.email.value
  setState((s) => ({
    ...s,
    subscribeRequest: loadingAsyncState(() => submitSubscription(email)),
  }))
}

export const FooterLayout = (props: FooterLayoutProps) => {
  const columns: FooterItem[][] =
    props.menuItems.kind === 'loaded'
      ? props.menuItems.value
          .filter((x) => x.LanguageCode == i18next.language)
          .reduce(
            (acc, x) =>
              acc.update(x.Column, (children = []) => [...children, x]),
            Map<string, FooterItem[]>()
          )
          .valueSeq()
          .toArray()
      : []
  return (
    <footer
      id="a11y-footer"
      className={`footer footer--${
        props.isLoggedIn ? 'logged-in' : 'logged-out'
      }`}
    >
      <div className="footer__top bg bg--dark">
        <div className="footer__top-inner container--m gutter--m">
          <nav
            className="nav nav--quicklinks"
            aria-label={i18next.t('a11y:navLabelQuicklinks')}
          >
            <ul className="footer__cols">
              {/* Logo */}
              <li className="footer__col footer__col--logo">
                <a
                  href="/"
                  className="footer__logo"
                  title={i18next.t('a11y:navigateHome')}
                >
                  <span className="sr-text">
                    {i18next.t('a11y:navigateHome')}
                  </span>
                  <img
                    src="/spa/images/empty.png"
                    data-src="/spa/images/logo-white-payoff.svg"
                    className="footer__image lazyload"
                    alt=""
                    width="280"
                    height="60"
                  />
                </a>
              </li>
              {/* Quicklinks */}
              <li className="footer__col footer__col--nav text--s">
                <ul className="nav__list">
                  {columns.map((column) =>
                    column.map((item) => (
                      <li className="nav__item" key={slugify(item.Title)}>
                        <ExternalLink
                          to={item.Url}
                          className="nav__link"
                        >
                          {item.Title}
                        </ExternalLink>
                      </li>
                    ))
                  )}
                </ul>
              </li>
              {/* Newsletter */}
              <li className="footer__col footer__col--newsletter data-hj-suppress">
                <form
                  className="form--newsletter"
                  onSubmit={(event) => handleSubmit(event, props.setState)}
                >
                  <div className="form__header text--styled">
                    <h3 className="form__title bg">
                      {i18next.t('footer:newsletterSubscriptionTitle')}
                    </h3>
                    <p className="form__intro text--s">
                      {i18next.t('footer:newsletterSubscriptionText')}
                    </p>
                  </div>

                  {props.subscribeRequest.kind == 'loaded' ? (
                    <FormNotification
                      status="success"
                      options={{
                        style: formSkeletonConfig.options.style,
                      }}
                      message={i18next.t(
                        'footer:newsletterSubscriptionSuccess'
                      )}
                    />
                  ) : props.subscribeRequest.kind === 'loading' ? (
                    <></>
                  ) : props.subscribeRequest.kind !== 'unloaded' ? (
                    <FormNotification
                      options={{
                        style: formSkeletonConfig.options.style,
                      }}
                      message={i18next.t('footer:newsletterSubscriptionFailed')}
                    />
                  ) : (
                    <div className="form__group">
                      <label
                        htmlFor="footer-newsletter-email"
                        className="sr-text"
                      >
                        {i18next.t('auth:email')}
                      </label>
                      <input
                        id="footer-newsletter-email"
                        type="email"
                        name="email"
                        inputMode="email"
                        className="form__input"
                        placeholder={i18next.t('auth:email')}
                        aria-invalid={false}
                        // aria-describedby="a11y-footer-newsletter-email-error"
                      />

                      {/* <Form.Control.Feedback
                        role="alert"
                        type="invalid"
                        id="a11y-footer-newsletter-email-error"
                        className={getClassName(formSkeletonConfig.options, 'formFieldFeedbackInvalidClassName', '')}
                      >
                        {i18next.t('auth:signupEmailError')}
                      </Form.Control.Feedback> */}

                      <button
                        className="form__button"
                        type="submit"
                        title={i18next.t('footer:newsletterSubscriptionButton')}
                      >
                        <span className="sr-text">
                          {i18next.t('footer:newsletterSubscriptionButton')}
                        </span>
                        <SVGIcon
                          className="form__icon container"
                          size={20}
                          iconId="arrow"
                        />
                      </button>
                    </div>
                  )}
                </form>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="footer__bottom">
        <div className="footer__bottom-inner container--m gutter--m">
          {/* Legal */}
          <nav
            className="footer__legal nav--legal text--xs"
            aria-label={i18next.t('a11y:navLabelLegal')}
          >
            <ul className="nav__list">
              <li className="nav__item" aria-hidden="true">
                &copy; Gastronomixs
              </li>
              <li className="nav__item">
                <ExternalLink
                  to={i18next.t('footer:legalCookieStatementUrl')}
                  className="nav__link"
                >
                  {i18next.t('footer:legalCookieStatementText')}
                </ExternalLink>
              </li>
              {/* <li className="nav__item">
                <ExternalLink
                  type="link"
                  to={i18next.t('footer:legalDisclaimerUrl')}
                  className="nav__link"
                >
                  {i18next.t('footer:legalDisclaimerText')}
                </ExternalLink>
              </li> */}
              <li className="nav__item">
                <ExternalLink
                  to={i18next.t('footer:legalTermsUrl')}
                  className="nav__link"
                >
                  {i18next.t('footer:legalTermsText')}
                </ExternalLink>
              </li>
              <li className="nav__item">
                <button className="nav__link" onClick={() => {
                  //@ts-ignore Check if the method exists as a function type
                  if (typeof window.UC_UI !== 'undefined' && typeof window.UC_UI.showSecondLayer === 'function') {
                      //@ts-ignore UC_UI is a global method, we ignore the error for that
                      window.UC_UI.showSecondLayer();
                  }}}>
                  {i18next.t('footer:legalCookieSettingsText')}
                </button>
              </li>
              {/* <li className="nav__item">
                <ExternalLink
                  type="link"
                  to={i18next.t('footer:legalSitemapUrl')}
                  className="nav__link"
                >
                  {i18next.t('footer:legalSitemapText')}
                </ExternalLink>
              </li> */}
            </ul>
          </nav>

          {/* Language switch (only visible when logged out) */}
          {!props.isLoggedIn && (
            <div className="footer__expand expand expand--lang-switch">
              <LanguageSwitch
                isExpanded={props.languageSwitchMode == 'open'}
                onExpand={() =>
                  props.setLanguageSwitchMode(
                    props.languageSwitchMode == 'open' ? 'closed' : 'open'
                  )
                }
                onLanguageChanged={() => props.onLanguageChanged()}
              />
            </div>
          )}

          {/* Socials */}
          <nav
            className="footer__socials nav--socials"
            aria-label={i18next.t('a11y:navLabelSocial')}
          >
            <ul className="nav__list">
              <li className="nav__item">
                <IconButtonSocial
                  kind="link"
                  iconId="facebook"
                  iconSize="24"
                  srText={i18next.t('footer:socialFollowGSXon', {
                    platform: 'Facebook',
                  })}
                  to={i18next.t('footer:socialFacebookUrl')}
                  target="_blank"
                />
              </li>
              <li className="nav__item">
                <IconButtonSocial
                  kind="link"
                  iconId="instagram"
                  iconSize="24"
                  srText={i18next.t('footer:socialFollowGSXon', {
                    platform: 'Instagram',
                  })}
                  to={i18next.t('footer:socialInstagramUrl')}
                  target="_blank"
                />
              </li>
              <li className="nav__item">
                <IconButtonSocial
                  kind="link"
                  iconId="youtube"
                  iconSize="28"
                  srText={i18next.t('footer:socialFollowGSXon', {
                    platform: 'YouTube',
                  })}
                  to={i18next.t('footer:socialYouTubeUrl')}
                  target="_blank"
                />
              </li>
              <li className="nav__item">
                <IconButtonSocial
                  kind="link"
                  iconId="twitter-x"
                  iconSize="30"
                  srText={i18next.t('footer:socialFollowGSXon', {
                    platform: 'Twitter',
                  })}
                  to={i18next.t('footer:socialTwitterUrl')}
                  target="_blank"
                />
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  )
}
